import { parse } from 'cookie';
import { IncomingMessage } from 'http';

import { Location as LocationType } from '@eventbrite/geo-service-pysoa-model';
import { EventbriteHeader } from '@eventbrite/webapp-constants';

import { getPlaceFromCoordinates } from '@/src/homepage/api/server/getPlaceFromCoordinates';
import { getLocationFromIP } from '@/src/homepage/api/server/getLocationFromIP';
import { getPlaceFromLocationSlug } from '@/src/homepage/api/geo/getPlaceFromLocationSlug';
import type { Location } from '@/src/homepage/types';

export const EMPTY_LOCATION: Location = {
    slug: null,
    placeId: null,
    latitude: null,
    longitude: null,
    currentPlace: null,
    currentPlaceParent: null,
    placeType: null,
    isOnline: null,
};

export const LOCATION_COOKIE = 'location';

export type GeoPlace = {
    id?: string;
    latitude?: number;
    longitude?: number;
    place_type?: string;
    location_slug?: string;
    localizations?: Record<string, { locality?: { full_name?: string }; region?: { full_name?: string } }>;
};

export function placeToLocation(place: GeoPlace): Location {
    const localized = place.localizations?.default;
    return {
        currentPlace: localized?.locality?.full_name || null,
        currentPlaceParent: localized?.region?.full_name || null,
        latitude: place.latitude || null,
        longitude: place.longitude || null,
        placeId: place.id || null,
        placeType: place.place_type || null,
        slug: place.location_slug || null,
        isOnline: !place.id,
    };
}

export const getUserLocationFromRequest = async (req: IncomingMessage | undefined) => {
    if (!req) {
        console.debug('EMPTY_LOCATION - No request object found in app context');
        return EMPTY_LOCATION;
    }

    let placeObj: Location | null = null;
    const cookieHeader = req.headers['cookie'] || '';
    const locationCookie = parse(cookieHeader)[LOCATION_COOKIE];

    if (locationCookie) {
        console.debug('COOKIE_LOCATION - trying to extract location from the cookie');
        try {
            const parsedLocation = JSON.parse(decodeURIComponent(locationCookie));
            placeObj = {
                slug: parsedLocation.slug,
                placeId: parsedLocation.place_id,
                latitude: parsedLocation.latitude,
                longitude: parsedLocation.longitude,
                currentPlace: parsedLocation.current_place,
                currentPlaceParent: parsedLocation.current_place_parent,
                placeType: parsedLocation.place_type,
                isOnline: parsedLocation.is_online || false,
            };
        } catch (e) {
            console.error(`Error parsing location cookie: ${locationCookie}`);
        }
    }

    if (placeObj && placeObj.placeId) {
        console.debug('COOKIE_LOCATION - found placeId in the cookie', placeObj);
        return placeObj;
    }

    // The edge resolves the viewer's location slug and includes it in the
    // homepage cache key; resolving the place from it keeps the SSR location
    // consistent with the cached document instead of re-deriving from
    // coordinates. Falls through when geo resolution is unavailable.
    const locationSlug = req.headers['eb-location-slug'] as string | undefined;
    if (locationSlug) {
        const place = (await getPlaceFromLocationSlug(locationSlug)) as GeoPlace | null;
        if (place?.id) {
            console.debug('EDGE_LOCATION - found placeId from the edge location slug', place);
            return placeToLocation(place);
        }
    }

    const lat = req.headers['cloudfront-viewer-latitude'] as string;
    const lon = req.headers['cloudfront-viewer-longitude'] as string;
    let parsedLat;
    let parsedLon;

    try {
        parsedLat = parseFloat(lat);
        parsedLon = parseFloat(lon);
    } catch (e) {
        console.error('Error parsing latitude and longitude', e);
    }

    /**
     * These checks cover the next scenarios:
     * 1. lat and long are present -> getPlace from coordinates OK
     * 2. lat and long are present but no place returned from getPlaceFromCoordinates->
     * fallback to IP and look up again using the IP coordinates with locality selector
     * if this fails, use the region
     * if this fails, use the country
     * if we did not get a place using region and country, use the lat and lon we have
     */
    if (parsedLat && parsedLon) {
        console.debug(
            'CF_HEADER_LOCATION - Found latitude and longitude in the CloudFront headers',
            parsedLat,
            parsedLon,
        );
        placeObj = await getPlaceFromCoordinates(parsedLat, parsedLon);

        if (placeObj && placeObj.placeId) {
            console.debug('CF_HEADER_LOCATION - Found placeId in the coordinates', placeObj);
            return placeObj;
        }

        // In case we cannot get a location from any of the fallbacks, using empty with the lat and lon we have
        placeObj = {
            ...EMPTY_LOCATION,
            latitude: parsedLat,
            longitude: parsedLon,
        };

        console.warn(
            'CF_HEADER_LOCATION - No place found for the coordinates, getting the location from the IP',
        );
    }

    const forwardedFor = req.headers['x-forwarded-for'] as string;
    const sourceIp = forwardedFor?.split(',')[0];

    if (sourceIp) {
        try {
            const location = await getLocationFromIP(
                sourceIp,
                req.headers[EventbriteHeader.EB_SOA_TOKEN] as string,
            );
            const locationLatitude = (location?.location as LocationType)?.latitude;
            const locationLongitude = (location?.location as LocationType)?.longitude;

            if (locationLatitude && locationLongitude) {
                console.debug(
                    'LOCATION - IP - Found location from IP',
                    locationLatitude,
                    locationLongitude,
                );
                parsedLat = locationLatitude;
                parsedLon = locationLongitude;
            }
        } catch (e) {
            console.error('Error getting location from IP', e);
        }
    }

    if (parsedLat && parsedLon) {
        placeObj = await getPlaceFromCoordinates(parsedLat, parsedLon, 'locality');
        if (placeObj && placeObj.placeId) {
            console.debug('CF_HEADER_LOCATION - LOCALITY - Found placeId from location', placeObj);
            return placeObj;
        }

        placeObj = await getPlaceFromCoordinates(parsedLat, parsedLon, 'region');
        if (placeObj && placeObj.placeId) {
            console.debug('CF_HEADER_LOCATION - REGION - Found placeId from location', placeObj);
            return placeObj;
        }

        placeObj = await getPlaceFromCoordinates(parsedLat, parsedLon, 'country');
        if (placeObj && placeObj.placeId) {
            console.debug('CF_HEADER_LOCATION - COUNTRY - Found placeId from location', placeObj);
            return placeObj;
        }
        // In case we cannot get a location from any of next fallbacks, updating the placeObj with the lat and lon we got from the location
        placeObj = {
            ...EMPTY_LOCATION,
            latitude: parsedLat,
            longitude: parsedLon,
        };
    }

    console.warn('NO_LOCATION_FOUND using coordinates as is', placeObj);

    return placeObj || EMPTY_LOCATION;
};
